import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import rehypeKatex from "rehype-katex";
import remarkMath from "remark-math";
import "katex/dist/katex.min.css"; // `rehype-katex` does not import the CSS for you

import CopyButton from "../Message/CopyButton";
import MermaidDiagram from "./MermaidDiagram";
import VegaLiteChart from "./VegaLiteChart";
import styles from "./Markdown.module.scss";

interface MarkdownProps {
  content: string;
  /**
   * Forwarded to MermaidDiagram / VegaLiteChart so they can defer rendering
   * until streaming completes (avoids running mermaid.parse / vega-embed
   * against partial code blocks).
   */
  isStreaming?: boolean;
  /**
   * Forwarded to VegaLiteChart so it can resolve `file://<file_id>` data URLs.
   */
  getAccessToken?: () => Promise<string>;
}

/**
 * The Markdown component renders a string of Markdown-formatted text into React
 * elements.
 */
export default React.memo(function Markdown({
  content,
  isStreaming,
  getAccessToken,
}: MarkdownProps) {
  // Replace \[ with $$ and \] with $$ for KaTeX compatibility.
  // https://community.openai.com/t/api-gpt-4-turbo-preview-has-output-in-latex-format-in-math/534293/10
  const katexReadyContent = content
    .replace(/\\\[/g, "$$$") // Replace all occurrences of \[ with $$
    .replace(/\\\]/g, "$$$") // Replace all occurrences of \] with $$
    .replace(/\\\(/g, "$$$") // Replace all occurrences of \( with $$
    .replace(/\\\)/g, "$$$"); // Replace all occurrences of \) with $$

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      children={katexReadyContent}
      components={{
        p: ({ node, children, ...props }) => (
          <p className="lh-copy f5" {...props}>
            {children}
          </p>
        ),
        ol: ({ node, children, ...props }) => {
          // Remove the 'ordered' prop as it causes a console warning:
          // "Warning: Received `true` for a non-boolean attribute `ordered`."
          // @ts-expect-error - ordered prop exists at runtime but not in types
          delete props.ordered;
          return (
            <ol className="lh-copy f5" {...props}>
              {children}
            </ol>
          );
        },
        li: ({ node, children, ...props }) => {
          // Remove the 'ordered' prop as it causes a console warning:
          // "Warning: Received `true` for a non-boolean attribute `ordered`."
          // @ts-expect-error - ordered prop exists at runtime but not in types
          delete props.ordered;
          return (
            <li className="lh-copy f5 mb2" {...props}>
              {children}
            </li>
          );
        },
        table: ({ node, children, ...props }) => {
          return (
            <div className={styles.tableWrapper}>
              <table className="f6 w-100" cellSpacing="0" {...props}>
                {children}
              </table>
            </div>
          );
        },
        th: ({ node, children, ...props }) => {
          return (
            <th className="fw6 bb tl pb2 pr3" {...props}>
              {children}
            </th>
          );
        },
        td: ({ node, children, ...props }) => {
          return (
            <td className="pv2 pr3 bb" {...props}>
              {children}
            </td>
          );
        },
        tbody: ({ node, children, ...props }) => {
          return (
            <tbody className="lh-copy" {...props}>
              {children}
            </tbody>
          );
        },
        a: ({ node, children, ...props }) => {
          return (
            <a
              {...props}
              target="_blank"
              rel="noopener noreferrer"
              className="link"
            >
              {children}
            </a>
          );
        },
        code({ node, className, children, ...props }) {
          const match = /language-([\w-]+)/.exec(className || "");
          const codeContent = String(children).replace(/\n$/, "");

          if (match && match[1] === "mermaid") {
            return (
              <MermaidDiagram code={codeContent} isStreaming={isStreaming} />
            );
          }

          if (match && (match[1] === "vega-lite" || match[1] === "vegalite")) {
            return (
              <VegaLiteChart
                code={codeContent}
                isStreaming={isStreaming}
                getAccessToken={getAccessToken}
              />
            );
          }

          if (match && match[1] === "vega") {
            return (
              <div className={styles.mermaidContainer}>
                <div className={styles.vegaUnsupported}>
                  Full Vega specs are not supported. Use <code>vega-lite</code>{" "}
                  instead.
                </div>
                <div className={styles.codeBlock}>
                  <SyntaxHighlighter
                    style={oneDark}
                    language="json"
                    customStyle={{
                      borderRadius: "0 0 0.25rem 0.25rem",
                      margin: "0px",
                    }}
                    PreTag="div"
                  >
                    {codeContent}
                  </SyntaxHighlighter>
                  <CopyButton content={codeContent} />
                </div>
              </div>
            );
          }

          return match ? (
            <div className={styles.codeBlock}>
              <div
                className={`${styles.codeBlockHeader} flex justify-end items-center w-100 pa2`}
              >
                <CopyButton content={codeContent} />
              </div>
              <SyntaxHighlighter
                {...(props as any)}
                children={codeContent}
                style={oneDark}
                customStyle={{
                  borderRadius: "0 0 0.25rem 0.25rem",
                  margin: "0px",
                }}
                language={match[1]}
                PreTag="div"
              />
            </div>
          ) : (
            <code {...props} className={`${className} code f6`}>
              {children}
            </code>
          );
        },
      }}
    />
  );
});
